import api from '../api';

/**
 * Utility class for logging user activities
 */
export class ActivityLogger {
  /**
   * Log a user activity
   * @param {string} activityType - Type of activity (from UserActivityService.ActivityType)
   * @param {string} title - Title of the activity
   * @param {string} description - Description of the activity
   * @param {string} iconType - Icon type (from UserActivityService.IconType)
   * @param {Object} metadata - Additional metadata (optional)
   */
  static async logActivity(activityType, title, description, iconType, metadata = null) {
    try {
      const payload = {
        activityType,
        title,
        description,
        iconType,
        metadata: metadata ? JSON.stringify(metadata) : null
      };

      await api.post('/user-activity/log', payload);
    } catch (error) {
      console.error('Failed to log activity:', error);
      // Don't throw error to avoid breaking the main functionality
    }
  }

  /**
   * Log lead-related activities
   */
  static async logLeadActivity(action, leadData, additionalInfo = {}) {
    const activities = {
      CREATE_LEAD: {
        activityType: 'CREATE_LEAD',
        title: 'Created New Lead',
        description: `Created lead for company: ${leadData.companyName || 'Unknown'}`,
        iconType: 'lead'
      },
      UPDATE_LEAD: {
        activityType: 'UPDATE_LEAD',
        title: 'Updated Lead',
        description: `Updated lead for company: ${leadData.companyName || 'Unknown'}`,
        iconType: 'edit'
      },
      DELETE_LEAD: {
        activityType: 'DELETE_LEAD',
        title: 'Deleted Lead',
        description: `Deleted lead for company: ${leadData.companyName || 'Unknown'}`,
        iconType: 'trash'
      },
      VIEW_LEAD_DETAIL: {
        activityType: 'VIEW_LEAD_DETAIL',
        title: 'Viewed Lead Details',
        description: `Viewed details for lead: ${leadData.companyName || 'Unknown'}`,
        iconType: 'eye'
      }
    };

    const activity = activities[action];
    if (activity) {
      await this.logActivity(
        activity.activityType,
        activity.title,
        activity.description,
        activity.iconType,
        { leadId: leadData.id, ...additionalInfo }
      );
    }
  }

  /**
   * Log task-related activities
   */
  static async logTaskActivity(action, taskData, leadData = null, additionalInfo = {}) {
    const activities = {
      CREATE_TASK: {
        activityType: 'CREATE_TASK',
        title: 'Created New Task',
        description: `Created task: ${taskData.title || 'Untitled Task'}`,
        iconType: 'task'
      },
      UPDATE_TASK: {
        activityType: 'UPDATE_TASK',
        title: 'Updated Task',
        description: `Updated task: ${taskData.title || 'Untitled Task'}`,
        iconType: 'edit'
      },
      DELETE_TASK: {
        activityType: 'DELETE_TASK',
        title: 'Deleted Task',
        description: `Deleted task: ${taskData.title || 'Untitled Task'}`,
        iconType: 'trash'
      },
      DOWNLOAD_TASK_FILE: {
        activityType: 'DOWNLOAD_TASK_FILE',
        title: 'Downloaded Task File',
        description: `Downloaded file: ${taskData.fileName || 'Unknown File'}`,
        iconType: 'download'
      }
    };

    const activity = activities[action];
    if (activity) {
      await this.logActivity(
        activity.activityType,
        activity.title,
        activity.description,
        activity.iconType,
        { 
          taskId: taskData.id, 
          leadId: leadData?.id, 
          ...additionalInfo 
        }
      );
    }
  }

  /**
   * Log service-related activities
   */
  static async logServiceActivity(action, serviceData, leadData = null, additionalInfo = {}) {
    const activities = {
      ADD_SERVICE_TO_LEAD: {
        activityType: 'ADD_SERVICE_TO_LEAD',
        title: 'Added Service to Lead',
        description: `Added service to lead: ${leadData?.companyName || 'Unknown Lead'}`,
        iconType: 'service'
      },
      UPDATE_LEAD_SERVICE: {
        activityType: 'UPDATE_LEAD_SERVICE',
        title: 'Updated Lead Service',
        description: `Updated service for lead: ${leadData?.companyName || 'Unknown Lead'}`,
        iconType: 'edit'
      },
      DELETE_LEAD_SERVICE: {
        activityType: 'DELETE_LEAD_SERVICE',
        title: 'Deleted Lead Service',
        description: `Deleted service from lead: ${leadData?.companyName || 'Unknown Lead'}`,
        iconType: 'trash'
      }
    };

    const activity = activities[action];
    if (activity) {
      await this.logActivity(
        activity.activityType,
        activity.title,
        activity.description,
        activity.iconType,
        { 
          serviceId: serviceData.id, 
          leadId: leadData?.id, 
          ...additionalInfo 
        }
      );
    }
  }

  /**
   * Log document-related activities
   */
  static async logDocumentActivity(action, documentData, leadData = null, additionalInfo = {}) {
    const activities = {
      UPLOAD_DOCUMENT: {
        activityType: 'UPLOAD_DOCUMENT',
        title: 'Uploaded Document',
        description: `Uploaded document: ${documentData.fileName || 'Unknown File'}`,
        iconType: 'upload'
      },
      DOWNLOAD_DOCUMENT: {
        activityType: 'DOWNLOAD_DOCUMENT',
        title: 'Downloaded Document',
        description: `Downloaded document: ${documentData.fileName || 'Unknown File'}`,
        iconType: 'download'
      },
      DELETE_DOCUMENT: {
        activityType: 'DELETE_DOCUMENT',
        title: 'Deleted Document',
        description: `Deleted document: ${documentData.fileName || 'Unknown File'}`,
        iconType: 'trash'
      }
    };

    const activity = activities[action];
    if (activity) {
      await this.logActivity(
        activity.activityType,
        activity.title,
        activity.description,
        activity.iconType,
        { 
          documentId: documentData.id, 
          leadId: leadData?.id, 
          ...additionalInfo 
        }
      );
    }
  }

  /**
   * Log proposal-related activities
   */
  static async logProposalActivity(action, proposalData, leadData = null, additionalInfo = {}) {
    const activities = {
      CREATE_PROPOSAL: {
        activityType: 'CREATE_PROPOSAL',
        title: 'Created Proposal',
        description: `Created proposal for lead: ${leadData?.companyName || 'Unknown Lead'}`,
        iconType: 'proposal'
      },
      UPDATE_PROPOSAL: {
        activityType: 'UPDATE_PROPOSAL',
        title: 'Updated Proposal',
        description: `Updated proposal for lead: ${leadData?.companyName || 'Unknown Lead'}`,
        iconType: 'edit'
      },
      DELETE_PROPOSAL: {
        activityType: 'DELETE_PROPOSAL',
        title: 'Deleted Proposal',
        description: `Deleted proposal for lead: ${leadData?.companyName || 'Unknown Lead'}`,
        iconType: 'trash'
      },
      VIEW_PROPOSALS: {
        activityType: 'VIEW_PROPOSALS',
        title: 'Viewed Proposals',
        description: `Viewed proposals for lead: ${leadData?.companyName || 'Unknown Lead'}`,
        iconType: 'eye'
      }
    };

    const activity = activities[action];
    if (activity) {
      await this.logActivity(
        activity.activityType,
        activity.title,
        activity.description,
        activity.iconType,
        { 
          proposalId: proposalData.id, 
          leadId: leadData?.id, 
          ...additionalInfo 
        }
      );
    }
  }

  /**
   * Log contract-related activities
   */
  static async logContractActivity(action, contractData, leadData = null, additionalInfo = {}) {
    const activities = {
      CREATE_CONTRACT: {
        activityType: 'CREATE_CONTRACT',
        title: 'Created Contract',
        description: `Created contract for lead: ${leadData?.companyName || 'Unknown Lead'}`,
        iconType: 'contract'
      },
      UPDATE_CONTRACT: {
        activityType: 'UPDATE_CONTRACT',
        title: 'Updated Contract',
        description: `Updated contract for lead: ${leadData?.companyName || 'Unknown Lead'}`,
        iconType: 'edit'
      },
      SIGN_CONTRACT: {
        activityType: 'SIGN_CONTRACT',
        title: 'Signed Contract',
        description: `Signed contract for lead: ${leadData?.companyName || 'Unknown Lead'}`,
        iconType: 'edit'
      },
      VIEW_CONTRACTS: {
        activityType: 'VIEW_CONTRACTS',
        title: 'Viewed Contracts',
        description: `Viewed contracts for lead: ${leadData?.companyName || 'Unknown Lead'}`,
        iconType: 'eye'
      }
    };

    const activity = activities[action];
    if (activity) {
      await this.logActivity(
        activity.activityType,
        activity.title,
        activity.description,
        activity.iconType,
        { 
          contractId: contractData.id, 
          leadId: leadData?.id, 
          ...additionalInfo 
        }
      );
    }
  }
}

export default ActivityLogger;
